import React, { Component } from 'react';

import debounce from 'lodash.debounce';

import { FilterAPI } from '@philips-emr/tws-components-api';
import { Expression } from '@philips-emr/tws-constants';
import { AwesomeComponent, ComponentType, ContainerProvider } from '@philips-emr/tws-core';
import { arrayOf, element, eventListener, expression, object, oneOfType, string, tuple } from '@philips-emr/tws-core/lib/component/prop-types';
import { msg } from '@philips-emr/tws-core/lib/services/message-service';
import { createAbort, isDefined, isEqualShallow, isFunction, isObject, noop, propagateData } from '@philips-emr/tws-utils';

import './filter.scss';

const { CLEAR_ALL, FILTER_BY } = Expression;

const acceptedTypes = [
  ComponentType.FORM,
];

@AwesomeComponent({ type: ComponentType.FORM, api: FilterAPI })
class Filter extends Component {

  static propTypes = {
    children: oneOfType([element, arrayOf(element)]).notEnumerable(),
    data: object.notEnumerable(),
    onBeforeCancel: eventListener,
    onBeforeSubmit: eventListener,
    onCancel: eventListener,
    onSubmit: eventListener,
    recordMapper: tuple([
      string.placeholder('field entry'),
      string.placeholder('value key'),
    ]),
    title: expression,
    uuid: string,
  };

  static defaultProps = {
    data: {},
    recordMapper: {},
    title: FILTER_BY,
  };

  state = {
    record: {},
  };

  componentDidMount() {
    this.updateRecordFromDataAndSubmit();
  }

  componentDidUpdate(prevProps) {
    const { data = {} } = this.props;

    if (!isEqualShallow(data, prevProps.data)) {
      this.updateRecordFromDataAndSubmit();
    }
  }

  updateRecordFromDataAndSubmit() {
    const { data = {} } = this.props;

    this.setState(({ record }) => ({
      record: { ...data, ...record },
    }), () => this.submitRecord());
  }

  getNormalizedRecord() {
    const { recordMapper } = this.props;
    const { record } = this.state;

    const entriesToObject = (obj, [key, value]) => ({ ...obj, [key]: value });
    const isDefinedNotNull = ([, value]) => isDefined(value) && value !== null;

    const mapEntryValue = (entry) => {
      const [key, value] = entry;

      if (!(key in recordMapper)) {
        return entry;
      }

      const mappedKey = recordMapper[key];
      const get = (obj, key) => key in obj ? obj[key] : obj.value || obj.key;

      if (isObject(value)) {
        return [key, get(value, mappedKey)];
      } else if (Array.isArray(value)) {
        const mappedValue = value.filter(isObject).map(item => get(item, mappedKey));
        return [key, mappedValue];
      }

      return entry;
    }

    const recordEntries = Object.entries(record);

    const normalizedRecord = recordEntries
      .filter(isDefinedNotNull) // remove undefined and null
      .map(mapEntryValue) // map value using recordMapper if necessary
      .reduce(entriesToObject, {}); // convert entries back to object

    return normalizedRecord;
  }

  submitRecord = debounce(() => {
    const { onBeforeSubmit, onSubmit } = this.props;

    const record = this.getNormalizedRecord();
    const abort = createAbort();

    isFunction(onBeforeSubmit) && onBeforeSubmit({ record, abort });

    abort.promise.then(aborted => {
      if (aborted) return;

      isFunction(onSubmit) && onSubmit({ record, abort: noop });
    });
  }, 150);

  onCancel = () => {
    const abort = createAbort();

    const { onBeforeCancel, onCancel } = this.props;

    isFunction(onBeforeCancel) && onBeforeCancel({ record: this.state.record, abort });

    abort.promise.then(aborted => {
      if (aborted) return;

      isFunction(onCancel) && onCancel({ record: this.state.record, abort: noop });
    });
  };

  onChange = ({ entry, value }) => {
    if (!entry) return;

    this.setState(({ record }) => ({
      record: { ...record, [entry]: value },
    }), this.submitRecord);
  };

  onClearAll = () => {
    this.setState({ record: this.props.data }, this.onCancel);
  };

  renderChildren() {
    const { children } = this.props;
    const { record } = this.state;

    return propagateData(children, record, {
      className: 'c-filter-field',
      onChange: this.onChange,
    });
  }

  render() {
    const { title, uuid } = this.props;
    const Container = ContainerProvider.provide(this);

    return (
      <div className="c-filter">
        {title && <div className="c-filter__title">{msg(title)}:</div>}

        <Container acceptedTypes={acceptedTypes} className="c-filter__items" uuid={uuid}>
          {this.renderChildren()}
          <button className="c-filter-field c-filter-field--clear-all" onClick={this.onClearAll}>{msg(CLEAR_ALL)}</button>
        </Container>
      </div>
    );
  }
}

export default Filter;
